import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import { CHECKBOX, FREE_TEXT, MATRICE, RADIO, TABLE } from '../../constants';
import { generateRandomString, getSurveyItem } from '../../helpers/surveyUtil';
import {
  AnswerProps,
  BlockProps,
  ChapterProps,
  InterrelatedFieldProps,
  QuestionAnswer,
  TabularAnswer,
} from '../../models/global';
import TextField from '../FormComponents/TextField';
import UserMatriceComponent from './UserMatrice';
import UserTableComponent from './UserTable';

interface FieldProps {
  chapterId: string;
  sectionId: string;
  block: BlockProps;
  language: string;
  questionAnswers: QuestionAnswer[];
  tabularAnswers: TabularAnswer[];
  changeQuestionAnswer: (
    value: string,
    blockFieldType: string,
    blockId: string,
    sectionId: string,
    optionId?: string,
    interrelatedFields?: InterrelatedFieldProps[],
  ) => void;
  changeTabularAnswer: (
    value: string | boolean,
    columnType: string,
    sectionId: string,
    columnId: string,
    rowId: string,
    rowKey: string,
    matrixType?: string,
  ) => void;
  setChapters: (chapters) => void;
  setTabularAnswers: (tabularAnswers) => void;
  setToast: (value: string) => void;
  chapters: ChapterProps[];
  readonly?: boolean;
  showLastSurvey?: boolean;
  isAdmin?: boolean;
  lastSurvey: { id: number; name: string };
}

function UserField({
  chapterId,
  sectionId,
  block,
  language,
  questionAnswers,
  changeQuestionAnswer,
  changeTabularAnswer,
  setChapters,
  setTabularAnswers,
  chapters,
  tabularAnswers,
  readonly,
  setToast,
  showLastSurvey,
  isAdmin,
  lastSurvey,
}: FieldProps) {
  const otherOption = (
    <Box>
      <Box display='flex'>
        <TextField
          id='answer'
          label='Other'
          variant='standard'
          multiline
          InputProps={{
            style: {
              fontSize: '0.875em',
            },
          }}
          onChange={e => {
            changeQuestionAnswer(e.target.value, block.fieldType, block.id, sectionId, null);
          }}
          value={questionAnswers.find(el => el.answer)?.answer || ''}
        />
        {!!questionAnswers.find(el => el.answer)?.answer && block.fieldType === RADIO && !isAdmin && (
          <Box mt={2} ml={2} onClick={() => changeQuestionAnswer('', block.fieldType, block.id, sectionId, null)}>
            <ClearIcon fontSize='small' />
          </Box>
        )}
      </Box>
    </Box>
  );

  const otherOptionOld = (
    <Box display='flex' alignItems='center'>
      <TextField
        id='answer'
        label='Other'
        variant='standard'
        multiline
        InputProps={{
          style: {
            fontSize: '0.875em',
          },
        }}
        onChange={() => {}}
        value={(block.answers as AnswerProps[])?.find(el => el.answer)?.answer || ''}
      />
      <ContentCopyIcon
        fontSize='small'
        sx={{ pointerEvents: 'visible' }}
        onClick={() => {
          navigator.clipboard.writeText((block.answers as AnswerProps[])?.find(el => el.answer)?.answer);
          setToast('Copied');
        }}
      />
    </Box>
  );

  const addRow = (chapterId: string, sectionId: string, blockId: string) => {
    const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
    if (!block.table.organizationRows) {
      block.table.organizationRows = [];
    }
    block.table.organizationRows.push({
      id: generateRandomString(true),
    });
    setChapters([...chapters]);
  };

  const removeRow = (chapterId: string, sectionId: string, blockId: string, rowId: string) => {
    const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
    block.table.organizationRows = block.table.organizationRows.filter(row => row.id !== rowId);
    setTabularAnswers(tabularAnswers.filter(el => el.organizationRowId !== rowId));
    setChapters([...chapters]);
  };

  // const isAnswer = (optionName: string) => {
  //   const option = block?.reference?.options.find(
  //     (item) =>
  //       item[language === 'English' ? 'name' : `name${language}`] ===
  //       optionName,
  //   );
  //   return block?.answers?.some((el) => el.optionId === option?.id);
  // };

  const checkbox = (
    <>
      {block?.options?.map((option, index) => (
        <Box key={index}>
          <Box className='options-block' pt={index !== 0 && '0 !important'}>
            <Checkbox
              sx={{ m: 0 }}
              size='small'
              onChange={() =>
                changeQuestionAnswer('', block.fieldType, block.id, sectionId, option.id, interrelatedFields)
              }
              checked={!!questionAnswers.find(el => el.optionId === option.id)}
            />
            <Typography variant='body2'>
              {language === 'English' || !option[`name${language}`] ? option.name : `${option[`name${language}`]}`}
            </Typography>
            {/* {showLastSurvey &&
              isAnswer(
                language === 'English'
                  ? option.name
                  : `${option[`name${language}`]}`,
              ) && (
                <Typography variant="h6" color="green" marginLeft={1} pt={0.5}>
                  *
                </Typography>
              )} */}
          </Box>
        </Box>
      ))}
      {block.hasOtherOption && (
        <Box className='options-block' pt={'0 !important'}>
          <Tooltip title={!questionAnswers.find(el => el.answer) ? 'Please fill value' : ''} placement='top'>
            <Checkbox
              sx={{ m: 0 }}
              size='small'
              onChange={() => {
                if (questionAnswers.find(el => el.answer)) {
                  changeQuestionAnswer('', block.fieldType, block.id, sectionId, null);
                }
              }}
              checked={!!questionAnswers.find(el => el.answer)}
            />
          </Tooltip>
          {otherOption}
        </Box>
      )}
    </>
  );

  const checkboxOld = showLastSurvey && (
    <Box className='reference-answer'>
      <Typography variant='body2'>
        Answer(s) from the last survey {lastSurvey?.name ? `(${lastSurvey?.name})` : ''}
      </Typography>
      {block?.reference?.options ? (
        block.reference.options.map((option, index) => (
          <Box key={index}>
            <Box className='options-block' pt={'0 !important'}>
              <Checkbox
                sx={{ m: 0 }}
                size='small'
                onChange={() => {}}
                checked={!!(block.answers as AnswerProps[])?.find(el => el.optionId === option.id)}
              />
              <Typography variant='caption'>
                {language === 'English' || !option[`name${language}`] ? option.name : `${option[`name${language}`]}`}
              </Typography>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant='caption' ml={1}>
          No answer submitted
        </Typography>
      )}
      {block.reference?.hasOtherOption && (
        <Box className='options-block' pt={'0 !important'}>
          <Checkbox
            sx={{ m: 0 }}
            size='small'
            onChange={() => {}}
            checked={!!(block.answers as AnswerProps[])?.find(el => el.answer)}
          />
          {otherOptionOld}
        </Box>
      )}
    </Box>
  );

  const radio = (
    <>
      {block?.options?.map((option, index) => (
        <Box key={index}>
          <Box display='flex' alignItems='center'>
            <Box className='options-block' pt={index !== 0 && '0 !important'}>
              <Radio
                onChange={() =>
                  changeQuestionAnswer('', block.fieldType, block.id, sectionId, option.id, interrelatedFields)
                }
                checked={!!questionAnswers.find(el => el.optionId === option.id)}
              />
              <Typography variant='body2'>
                {language === 'English' || !option[`name${language}`] ? option.name : `${option[`name${language}`]}`}
              </Typography>
              {/* {showLastSurvey &&
                isAnswer(
                  language === 'English'
                    ? option.name
                    : `${option[`name${language}`]}`,
                ) && (
                  <Typography
                    variant="h6"
                    color="green"
                    marginLeft={1}
                    pt={0.5}
                  >
                    *
                  </Typography>
                )} */}
            </Box>
            {!!questionAnswers.find(el => el.optionId === option.id) && !isAdmin && (
              <Box
                mt={index === 0 ? 3 : 0}
                ml={2}
                onClick={() => changeQuestionAnswer('', block.fieldType, block.id, sectionId, null, interrelatedFields)}
              >
                <ClearIcon fontSize='small' />
              </Box>
            )}
          </Box>
        </Box>
      ))}
      {block.hasOtherOption && (
        <Box className='options-block' pt={'0 !important'}>
          <Tooltip title={!questionAnswers[0]?.answer ? 'Please fill value' : ''} placement='top'>
            <Radio checked={!!questionAnswers[0]?.answer} />
          </Tooltip>
          {otherOption}
        </Box>
      )}
    </>
  );

  const radioOld = showLastSurvey && (
    <Box className='reference-answer'>
      <Typography variant='body2'>
        Answer from the last survey {lastSurvey?.name ? `(${lastSurvey?.name})` : ''}
      </Typography>
      {block?.reference?.options ? (
        block.reference.options.map((option, index) => (
          <Box key={index}>
            <Box display='flex' alignItems='center'>
              <Box className='options-block' pt={'0 !important'}>
                <Radio
                  onChange={() =>
                    changeQuestionAnswer('', block.fieldType, block.id, sectionId, option.id, interrelatedFields)
                  }
                  checked={!!(block.answers as AnswerProps[]).find(el => el.optionId === option.id)}
                  size='small'
                />
                <Typography variant='caption'>
                  {language === 'English' || !option[`name${language}`] ? option.name : `${option[`name${language}`]}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant='caption' ml={1}>
          No answer submitted
        </Typography>
      )}
      {block.reference?.hasOtherOption && (
        <Box className='options-block' pt={'0 !important'}>
          <Radio checked={!!block.answers[0]?.answer} />
          {otherOptionOld}
        </Box>
      )}
    </Box>
  );

  const freeText = (
    <TextField
      id='answer'
      label='Answer'
      variant='outlined'
      multiline
      rows={4}
      sx={{ mt: 2, width: '100%' }}
      InputProps={{
        style: {
          fontSize: '0.875em',
        },
      }}
      maxLength={1000}
      onChange={e => changeQuestionAnswer(e.target.value, block.fieldType, block.id, sectionId)}
      value={questionAnswers[0]?.answer || ''}
    />
  );

  const freeTextOld = showLastSurvey && (
    <Box className='reference-answer' style={{ pointerEvents: 'visible' }}>
      <Typography variant='body2'>
        Answer from the last survey {lastSurvey?.name ? `(${lastSurvey?.name})` : ''}
      </Typography>
      {block.answers?.[0]?.answer ? (
        <Typography className='free-text-value'>
          {block.answers[0].answer}
          <ContentCopyIcon
            sx={{ verticalAlign: 'middle', ml: 1, fontSize: 15 }}
            onClick={() => {
              navigator.clipboard.writeText(block.answers[0].answer);
              setToast('Copied');
            }}
          />
        </Typography>
      ) : (
        <Typography variant='caption' ml={1}>
          No answer submitted
        </Typography>
      )}
    </Box>
  );

  const interrelatedFields = (block?.options || []).map(el => el.interrelatedFields).flat(2);

  switch (block.fieldType) {
    case CHECKBOX:
      return (
        <>
          {checkbox}
          {checkboxOld}
        </>
      );
    case RADIO:
      return (
        <>
          {radio}
          {radioOld}
        </>
      );
    case FREE_TEXT:
      return (
        <>
          {freeText}
          {freeTextOld}
        </>
      );
    case TABLE:
      return (
        <>
          <UserTableComponent
            table={block.table}
            language={language}
            changeTabularAnswer={changeTabularAnswer}
            sectionId={sectionId}
            addRow={() => addRow(chapterId, sectionId, block.id)}
            removeRow={rowId => removeRow(chapterId, sectionId, block.id, rowId)}
            tabularAnswers={tabularAnswers}
            block={block}
            readonly={readonly}
            marginTop={5}
            isAdmin={isAdmin}
          />
          {showLastSurvey && (
            <Box className='reference-answer'>
              <Typography variant='body2'>
                Table from the last survey {lastSurvey?.name ? `(${lastSurvey?.name})` : ''}
              </Typography>
              {block.referenceTable ? (
                <UserTableComponent
                  table={block.referenceTable}
                  language={language}
                  changeTabularAnswer={() => {}}
                  sectionId={sectionId}
                  addRow={() => {}}
                  removeRow={() => {}}
                  tabularAnswers={block.answers as TabularAnswer[]}
                  block={block}
                  readonly
                  marginTop={1}
                  isReference
                />
              ) : (
                <Typography variant='caption' ml={1}>
                  No answer submitted
                </Typography>
              )}
            </Box>
          )}
        </>
      );
    case MATRICE:
      return (
        <>
          <UserMatriceComponent
            matrice={block.matrice}
            language={language}
            readonly={readonly}
            changeTabularAnswer={changeTabularAnswer}
            tabularAnswers={tabularAnswers}
            sectionId={sectionId}
            marginTop={5}
          />
          {showLastSurvey && (
            <Box className='reference-answer'>
              <Typography variant='body2'>
                Matrix from the last survey {lastSurvey?.name ? `(${lastSurvey?.name})` : ''}
              </Typography>
              {block.referenceMatrice ? (
                <UserMatriceComponent
                  matrice={block.referenceMatrice}
                  language={language}
                  changeTabularAnswer={() => {}}
                  tabularAnswers={block.answers as TabularAnswer[]}
                  sectionId={sectionId}
                  readonly
                  marginTop={1}
                  isReference
                />
              ) : (
                <Typography variant='caption' ml={1}>
                  No answer submitted
                </Typography>
              )}
            </Box>
          )}
        </>
      );
  }
}

export default UserField;
